import { Task } from "redux-saga";

import { DataAttributesProps } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import { IAttrValue, IconCode } from "@multiweb/types/contracts";
import { UUID } from "Global/Base/IDs/UUID";
import { IOWObject } from "OW.Remoting/Remoting/DataTypes/IOWObject";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { IconEditProps } from "UI/UI.React/components/IconEdit";
import { iconEditConnectorSaga } from "UI/UI.React/components/IconEdit/saga";
import { IEditableControl, IMultiEdit, IRequiresActualObject } from "UI/UI/UI/Controls/ControlInterfaces";
import { ControlUtils } from "UI/UI/UI/Controls/ControlUtils";
import { EditableControlConnector } from "../EditableControlConnector";
import { GenericControlConnectorAttrs } from "../GenericControlConnector";

const { runSaga } = bridge;
const IconRatio = 0.6;

/**
 * Rozšíření konektoru EditableControlConnector pro napojení komponenty IconEdit do puvodní multiweb aplikace.
 */
export class IconEditConnector extends EditableControlConnector implements IEditableControl, IRequiresActualObject, IMultiEdit {
    /** Název atributu vazby */
    public readonly assocName?: string;
    /** Kód formuláře pro výběr záznamů */
    public readonly editWindow: string;
    /** Velikost ikony */
    private readonly _iconSize?: number;

    private readonly _uuid: string;

    private readonly _sagaTask: Task;

    constructor(attrs: GenericControlConnectorAttrs) {
        super(attrs, false);
        const assocName = ControlUtils.getDataAttr("AssocName", this.element);
        if (assocName) {
            this.assocName = assocName.replace(" ", "_");
        }
        this.editWindow = ControlUtils.getDataAttr("EditWindow", this.element);
        this._uuid = UUID.generate();
        this._iconSize = Math.min(parseInt(ControlUtils.getDataAttr("Width", this.element)), parseInt(ControlUtils.getDataAttr("Height", this.element)) * IconRatio);
        this._sagaTask = runSaga(iconEditConnectorSaga, this._uuid, this.editWindow, this.assocName, this.getAssocNameObject, this.setAssocNameObject, this.isEnabled);
    }

    public override get actualObject(): OWObject {
        return this._actualObject;
    }

    public override set actualObject(actualObject: OWObject | null) {
        super.actualObject = actualObject;
    }

    private isEnabled = () => {
        return this.enabled;
    };

    /** Nastavení assocName vazby, spuštění validace a v případě multi editace nastavení správné hodnoty na controlValue */
    public setAssocNameObject = async (result: readonly IAttrValue[] | undefined) => {
        if (Array.isArray(result) && result.length !== 0) {
            this.actualObject.setValue(this.assocName, result[0].valueOf());
            this.validityChanged.trigger(this, { valid: true });
            if (this.multiEditActive) {
                const assocObject = await this.getAssocNameObject() as IOWObject;
                const iconCode = await assocObject.getValue(this.attrName.replace(this.assocName + ".", "")) as IconCode;
                this.controlValue = iconCode;
            }
        }
    };

    public getAssocNameObject = () => {
        if (this.assocName && this.actualObject) {
            return this.actualObject.getValue(this.assocName);
        }
        return undefined;
    };

    protected override _getRenderProps(): IconEditProps & DataAttributesProps {
        return {
            ...super._getRenderProps(),
            iconSize: this._iconSize,
            uuid: this._uuid,
        };
    }

    public override dispose(): void {
        super.dispose();
        this._sagaTask.cancel();
    }
}
